import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { api } from '../../services/api';
import { DIGITAL_UPLOAD_ALLOWED_EXTENSIONS } from '../../helpers/digital-upload-allowed-extensions';
import {
  FILE_TYPE_LIMITS,
  ALLOWED_FILE_TYPES,
} from '../../utils/constants';
import {
  validateFileSize,
  validateFileType,
  validateFileName,
  validateFileExtension,
  getFileTypeLimitByName,
} from '../../utils/validation';

/** Digital file is an image if MIME is image/* or extension maps to IMAGE limits (handles empty file.type). */
const isImageFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  const limit = getFileTypeLimitByName(file.name);
  return limit === FILE_TYPE_LIMITS.IMAGE;
};

const DIGITAL_PREVIEW_THUMB_MAX = 560;
const DIGITAL_PREVIEW_JPEG_QUALITY = 0.85;

/** Resized JPEG data URL for persisting a small preview in checkout_json (internalFilename is not a display URL). */
const fileToJpegThumbnailDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      const max = DIGITAL_PREVIEW_THUMB_MAX;
      if (width > max || height > max) {
        if (width >= height) {
          height = Math.round((height * max) / width);
          width = max;
        } else {
          width = Math.round((width * max) / height);
          height = max;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not available'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      try {
        resolve(canvas.toDataURL('image/jpeg', DIGITAL_PREVIEW_JPEG_QUALITY));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for preview'));
    };
    img.src = objectUrl;
  });

const FileUpload = ({
  label,
  value,
  onChange,
  accept,
  maxSize,
  type = 'image',
  disabled = false,
  onInternalFilenameChange,
  onFileMetadataChange,
  digitalFilePreviewDataUrl = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(value || '');
  const [localImagePreviewUrl, setLocalImagePreviewUrl] = useState('');
  const fileInputRef = useRef(null);
  /** Tracks blob URL for digital image preview — revoke only here + on unmount, not in a [url] effect (avoids revoking the active URL under Strict Mode / effect ordering). */
  const localObjectUrlRef = useRef(null);

  const revokeLocalObjectUrl = () => {
    if (localObjectUrlRef.current) {
      URL.revokeObjectURL(localObjectUrlRef.current);
      localObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => revokeLocalObjectUrl();
  }, []);

  // Keep preview in sync when value is set externally (e.g. checkout state loaded from API).
  useEffect(() => {
    setPreview(value || '');
    if (!value) {
      revokeLocalObjectUrl();
      setLocalImagePreviewUrl('');
    }
  }, [value]);

  const resolveFileSizeLimit = (file) => {
    if (maxSize) return maxSize;
    switch (type) {
      case 'digital': {
        return getFileTypeLimitByName(file.name) || FILE_TYPE_LIMITS.ZIP;
      }
      case 'image':
        return FILE_TYPE_LIMITS.IMAGE;
      case 'document':
        return FILE_TYPE_LIMITS.DOCUMENT;
      case 'audio':
        return FILE_TYPE_LIMITS.AUDIO;
      case 'zip':
        return FILE_TYPE_LIMITS.ZIP;
      default:
        return FILE_TYPE_LIMITS.IMAGE;
    }
  };

  const getAllowedTypes = () => {
    if (accept) {
      return accept.split(',').map((t) => t.trim());
    }
    switch (type) {
      case 'image':
        return ALLOWED_FILE_TYPES.IMAGE;
      case 'digital':
        return DIGITAL_UPLOAD_ALLOWED_EXTENSIONS;
      case 'document':
        return ALLOWED_FILE_TYPES.DOCUMENT;
      case 'audio':
        return ALLOWED_FILE_TYPES.AUDIO;
      case 'zip':
        return ALLOWED_FILE_TYPES.ZIP;
      default:
        return ALLOWED_FILE_TYPES.IMAGE;
    }
  };

  const handleFileSelect = async (file) => {
    setError('');

    // Validate file name
    const nameError = validateFileName(file.name);
    if (nameError) {
      setError(nameError);
      return;
    }

    // Validate file type
    const typeError =
      type === 'digital'
        ? validateFileExtension(file.name, DIGITAL_UPLOAD_ALLOWED_EXTENSIONS)
        : validateFileType(file, getAllowedTypes());
    if (typeError) {
      setError(typeError);
      return;
    }

    // Validate file size
    const sizeError = validateFileSize(file, resolveFileSizeLimit(file));
    if (sizeError) {
      setError(sizeError);
      return;
    }

    revokeLocalObjectUrl();
    if (type === 'digital' && isImageFile(file)) {
      const url = URL.createObjectURL(file);
      localObjectUrlRef.current = url;
      setLocalImagePreviewUrl(url);
    } else {
      setLocalImagePreviewUrl('');
    }

    // Upload file (product images use the Account Manager image library)
    setUploading(true);
    try {
      let response;
      if (type === 'image') {
        response = await api.uploadImage(file);
      } else if (type === 'digital') {
        response = await api.uploadDigitalFile(file);
      } else {
        response = await api.uploadFile(file, type);
      }
      if (response.success) {
        const fileUrl = response.data?.url || response.data?.internalFilename;
        if (!fileUrl) {
          setError('Upload succeeded but no file URL was returned.');
          return;
        }
        setPreview(fileUrl);
        onChange(fileUrl);
        if (type === 'digital' && typeof onInternalFilenameChange === 'function') {
          onInternalFilenameChange(response.data?.internalFilename || fileUrl);
        }
        if (type === 'digital' && typeof onFileMetadataChange === 'function') {
          let imagePreviewDataUrl = '';
          if (isImageFile(file)) {
            try {
              imagePreviewDataUrl = await fileToJpegThumbnailDataUrl(file);
            } catch {
              imagePreviewDataUrl = '';
            }
          }
          onFileMetadataChange({
            contentType:
              response.data?.content_type ||
              file.type ||
              'application/octet-stream',
            fileName: response.data?.filename || file.name,
            contentLength: response.data?.content_length ?? file.size,
            imagePreviewDataUrl,
          });
        }
      } else {
        setError('Failed to upload file. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    revokeLocalObjectUrl();
    setLocalImagePreviewUrl('');
    setPreview('');
    onChange('');
    if (typeof onInternalFilenameChange === 'function') {
      onInternalFilenameChange('');
    }
    if (type === 'digital' && typeof onFileMetadataChange === 'function') {
      onFileMetadataChange({
        contentType: '',
        fileName: '',
        imagePreviewDataUrl: '',
      });
    }
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const dropText =
    type === 'image'
      ? 'Drop your product image file here, or '
      : type === 'digital'
        ? 'Drop your digital file here, or '
        : 'Drop your file here, or ';

  const showPreviewPanel =
    Boolean(preview) ||
    (type === 'digital' &&
      (Boolean(localImagePreviewUrl) || Boolean(digitalFilePreviewDataUrl)));

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      
      {showPreviewPanel ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: 200,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 1,
          }}
        >
          {type === 'image' ? (
            <img
              src={preview}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : type === 'digital' &&
            (localImagePreviewUrl || digitalFilePreviewDataUrl) ? (
            <img
              src={localImagePreviewUrl || digitalFilePreviewDataUrl}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                minHeight: 200,
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
          )}
          <IconButton
            onClick={handleRemove}
            disabled={disabled || uploading}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              p: 1,
              textAlign: 'center',
            }}
          >
            <Button
              size="small"
              onClick={handleBrowseClick}
              disabled={disabled || uploading}
              sx={{ color: 'white', minWidth: 'auto' }}
            >
              UPLOAD
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed #e0e0e0',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
            cursor: disabled || uploading ? 'not-allowed' : 'pointer',
            backgroundColor: disabled || uploading ? 'action.disabledBackground' : 'background.paper',
            '&:hover': {
              borderColor: disabled || uploading ? '#e0e0e0' : 'primary.main',
            },
          }}
          onClick={disabled || uploading ? undefined : handleBrowseClick}
        >
          {uploading ? (
            <CircularProgress />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                {dropText}
                <Button
                  component="span"
                  sx={{ textDecoration: 'underline', p: 0, minWidth: 'auto' }}
                  disabled={disabled}
                >
                  Browse
                </Button>
              </Typography>
              {type === 'digital' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Max size: Images 4MB, Documents 40MB, Audio 100MB, Video 1GB, Zip 1GB.
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept={accept || getAllowedTypes().join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />
      
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileUpload;
